// Shared services data — used by Home and Services pages.
// CRITICAL: Demand Generation & ABM "Learn more" links to https://www.revgrow-services.co.uk (external).
// `i18nKey` fields point at nested keys under `servicesData.<key>` in the locales.
export const SERVICES = [
  {
    id: "go-to-market-strategy",
    i18nKey: "goToMarket",
    number: "01",
    title: "Go-to-Market Strategy",
    tagline: "Uncover and prioritise the highest-impact opportunities.",
    description:
      "We combine market insight with rigorous, data-driven analysis to deliver GTM strategies that accelerate revenue and sharpen your competitive edge across every buyer segment.",
    capabilities: [
      "Opportunity Assessment",
      "Strategic Prioritisation",
      "Channel & Route-to-Market Design",
      "Buyer Journey Mapping",
      "Revenue Model Blueprinting",
    ],
    href: "/services",
    external: false,
  },
  {
    id: "market-customer-insights",
    i18nKey: "insights",
    number: "02",
    title: "Market & Customer Insights",
    tagline: "Turn intelligence into competitive advantage.",
    description:
      "Your best growth opportunities are hidden in plain sight — inside market trends, competitor behaviour, and customer needs. We transform raw data into clear, actionable intelligence.",
    capabilities: [
      "Market & Customer Research",
      "Competitive Intelligence",
      "Product & Pricing Analysis",
      "ICP & Segmentation",
      "Voice-of-Customer Programs",
    ],
    href: "/services",
    external: false,
  },
  {
    id: "demand-generation-abm",
    i18nKey: "abm",
    number: "03",
    title: "Demand Generation & ABM",
    tagline: "Blend demand gen and account-based plays for maximum impact.",
    description:
      "We craft targeted, multi-channel campaigns that connect with the right buyers at the right time — orchestrated across sales, marketing, and RevOps for measurable pipeline lift.",
    capabilities: [
      "Demand & ABM Strategy",
      "Target Account Selection & Prioritisation",
      "Data Health & Enrichment",
      "Full-Funnel Campaign Design",
      "Sales & Marketing Orchestration",
      "Pipeline Attribution & Reporting",
    ],
    href: "https://www.revgrow-services.co.uk",
    external: true,
  },
  {
    id: "campaign-media-management",
    i18nKey: "media",
    number: "04",
    title: "Campaign & Media Management",
    tagline: "Get your message in front of the right audience — every time.",
    description:
      "We plan, buy, and manage media to maximise reach, relevance, and return. From programmatic to premium editorial placements, every impression is engineered to convert.",
    capabilities: [
      "Media Planning & Buying",
      "Campaign Activation & Management",
      "ABM Platform Management",
      "Paid Social & Search",
      "Programmatic Display",
    ],
    href: "/services",
    external: false,
  },
  {
    id: "creative-content",
    i18nKey: "creative",
    number: "05",
    title: "Creative & Content",
    tagline: "Inspire action with ideas that stick.",
    description:
      "From creative campaigns to content strategies, we design experiences that ignite curiosity and accelerate the buyer journey — with the craft of an agency and the discipline of a growth team.",
    capabilities: [
      "Campaign Creative Platforms",
      "Design & Copywriting",
      "Content Strategy & Marketing",
      "Brand Storytelling",
      "Sales Enablement Assets",
    ],
    href: "/services",
    external: false,
  },
];

export const CHALLENGES = [
  { number: "01", i18nKey: "c1", title: "Mounting Competition", body: "Markets are more crowded than ever, making differentiation the difference between winning and disappearing." },
  { number: "02", i18nKey: "c2", title: "Evolving Buyer Expectations", body: "B2B buyers demand more value, faster delivery, and consumer-grade experiences at every stage of the journey." },
  { number: "03", i18nKey: "c3", title: "Disconnected Data", body: "Information silos across sales, marketing and product prevent the clear insights that unlock smart, timely decisions." },
  { number: "04", i18nKey: "c4", title: "Fragmented Teams", body: "Disconnected efforts dilute impact — waste resources, blur ownership, and slow the compound effect of alignment." },
  { number: "05", i18nKey: "c5", title: "Pressure for Accountability", body: "Every investment must show clear, measurable ROI — with transparent reporting the board and CFO can defend." },
];

export const CASE_STUDIES = [
  {
    slug: "fintech-scaleup",
    industry: "Fintech · Series C",
    client: "Halcyon Financial",
    duration: "9 months · 2024–25",
    services: ["Demand Generation & ABM", "Go-to-Market Strategy", "Analytics & RevOps"],
    title: "How a fintech scaleup unlocked £48M in qualified pipeline in 9 months",
    metric: "+312%",
    metricLabel: "MQL-to-SQL conversion",
    metrics: [
      { value: "£48M", label: "Qualified pipeline generated" },
      { value: "+312%", label: "MQL → SQL conversion" },
      { value: "220", label: "Named target accounts" },
      { value: "4.2x", label: "Return on media invested" },
    ],
    excerpt:
      "We rebuilt the ICP, re-scoped the ABM programme around 220 named accounts, and orchestrated a full-funnel campaign engine that transformed the revenue trajectory.",
    image:
      "https://images.unsplash.com/photo-1591696205602-2f950c417cb9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2ODh8MHwxfHNlYXJjaHwxfHxidXNpbmVzcyUyMGdyb3d0aCUyMGdyYXBoJTIwbW9kZXJufGVufDB8fHx8MTc4NDEyNTQyNnww&ixlib=rb-4.1.0&q=85",
    challenge:
      "Halcyon had a great product and a Series C balance sheet, but the top of the funnel was leaking. Sales was closing 68% of qualified opportunities — the problem wasn't conversion, it was that not enough of the right accounts were making it to the pipeline in the first place. The internal marketing team had run itself ragged trying to do everything for everyone.",
    approach: [
      "Rebuilt the ICP from first principles using firmographic, technographic and intent data — collapsing a 12-persona map into a focused 220-account programme.",
      "Restructured the demand-gen engine around three orchestrated plays: air cover (thought leadership), pressure (paid ABM), and precision (1:1 sales-led outreach).",
      "Stood up a shared pipeline dashboard between marketing, SDRs and account executives — one number, one weekly ritual, one owner per stage.",
      "Rebalanced media investment away from broad performance channels toward LinkedIn ABM, industry newsletters, and bespoke event experiences.",
    ],
    outcomes:
      "Nine months in, Halcyon had £48M in qualified pipeline, a 4.2x return on media invested, and — critically — a leadership team that no longer argued about attribution on Monday morning. The programme is now the operating template for their EMEA and North American expansion.",
    testimonial: {
      quote:
        "RevGrow didn't hand us a deck and walk away. They embedded with the team, rebuilt the pipeline model, and gave the board something they hadn't seen in five years — a growth plan that actually shipped.",
      author: "Ellen Marchetti",
      role: "CRO · Halcyon Financial",
    },
  },
  {
    slug: "enterprise-saas",
    industry: "Enterprise SaaS · EMEA",
    client: "Northwind Platform",
    duration: "14 weeks · 2024",
    services: ["Go-to-Market Strategy", "Market & Customer Insights", "Creative & Content"],
    title: "A European enterprise SaaS platform pierces the North American market",
    metric: "£12.4M",
    metricLabel: "Net-new ARR in year 1",
    metrics: [
      { value: "£12.4M", label: "Net-new ARR (year 1)" },
      { value: "14", label: "Weeks to first pipeline" },
      { value: "38", label: "Named enterprise wins" },
      { value: "0", label: "Existing US brand recognition" },
    ],
    excerpt:
      "Our GTM strategy team designed and executed the market-entry blueprint — from ICP to channel model — and stood up demand generation infrastructure in 14 weeks.",
    image:
      "https://images.unsplash.com/photo-1523875194681-bedd468c58bf?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTN8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMGJ1c2luZXNzJTIwc3RyYXRlZ3l8ZW58MHx8fHwxNzg0MTI1NDA3fDA&ixlib=rb-4.1.0&q=85",
    challenge:
      "Northwind was a European category leader with almost zero brand recognition in North America. The board had approved a $9M market-entry investment and given the CEO 18 months to prove it. The internal team had no US GTM experience — and no time to hire one.",
    approach: [
      "Ran a five-week market diagnostic across the top 3 US verticals — sizing, competitor mapping, buyer interviews, channel economics.",
      "Designed a hybrid GTM model: direct enterprise sales in two verticals, partner-led motion in the third — with a shared marketing engine underneath.",
      "Built a category-defining narrative platform, launched with a flagship report distributed to 4,000 handpicked buyers via partners and media.",
      "Instrumented the entire funnel in HubSpot + Salesforce from day one, so every dollar of the $9M was traceable to pipeline outcome.",
    ],
    outcomes:
      "Fourteen weeks after kick-off, Northwind had £3.1M of qualified pipeline in the US. By month twelve, that had converted into £12.4M in net-new ARR and 38 named enterprise wins — enough for the board to double down and approve a Series D bridge round.",
    testimonial: {
      quote:
        "We treated RevGrow as an extension of the exec team, not a vendor. That's the only way market entry works when the clock is this short.",
      author: "Simon Achterberg",
      role: "CEO · Northwind Platform",
    },
  },
  {
    slug: "b2b-industrial",
    industry: "B2B Industrial · PE-Backed",
    client: "Meridian Industrial",
    duration: "12 months · 2023–24",
    services: ["Creative & Content", "Campaign & Media Management", "Demand Generation & ABM"],
    title: "Turning a legacy industrial brand into a modern demand engine",
    metric: "4.7x",
    metricLabel: "Return on ad spend",
    metrics: [
      { value: "4.7x", label: "Return on ad spend" },
      { value: "+186%", label: "Inbound qualified leads" },
      { value: "£8.9M", label: "Attributable revenue" },
      { value: "3", label: "Category-defining launches" },
    ],
    excerpt:
      "We reset the brand narrative, launched a category-defining thought-leadership platform, and reallocated media investment into a data-driven ABM programme.",
    image:
      "https://images.unsplash.com/photo-1514557718210-26e452f8fab0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzZ8MHwxfHNlYXJjaHwzfHxtb2Rlcm4lMjBjb3Jwb3JhdGUlMjBvZmZpY2UlMjBsb25kb258ZW58MHx8fHwxNzg0MTI1NDA2fDA&ixlib=rb-4.1.0&q=85",
    challenge:
      "Meridian was a 40-year-old industrial specialist recently acquired by a mid-market PE fund. The mandate: double EBITDA in 36 months. Marketing was three people running trade shows and a PDF catalogue. The category was drowning in commoditised competitors.",
    approach: [
      "Repositioned the brand around a sharp category POV — 'The operating system for industrial procurement' — supported by an original research programme.",
      "Launched three category-defining assets in 12 months: a benchmark study, an industry podcast, and a live executive roundtable series.",
      "Rebuilt the media mix from 90% trade-show to 55% digital ABM + 25% intent-based programmatic + 20% flagship events.",
      "Wired the SDR team into a shared account plan with sales — the industrial equivalent of RevOps discipline.",
    ],
    outcomes:
      "Twelve months later, Meridian's inbound pipeline was up 186%, ROAS hit 4.7x, and the sponsor was in early talks about a secondary. The 'boring industrial player' had become the one everyone else was benchmarking against.",
    testimonial: {
      quote:
        "The best money we've spent as a portfolio company. RevGrow didn't just modernise our marketing — they gave the whole business a new operating tempo.",
      author: "David Ruthven",
      role: "Operating Partner · Argent Capital",
    },
  },
];

export const BLOG_POSTS = [
  {
    slug: "abm-in-2026",
    category: "Demand Generation",
    date: "12 January 2026",
    title: "ABM in 2026 — why the account list is the new brand strategy",
    excerpt:
      "The definition of a target account is quietly evolving. Here's how modern revenue teams are re-thinking selection, prioritisation, and orchestration.",
    readTime: "8 min read",
    author: "Priya Menon",
    authorRole: "Partner · Demand Generation",
    image:
      "https://images.unsplash.com/photo-1591696205602-2f950c417cb9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2ODh8MHwxfHNlYXJjaHwxfHxidXNpbmVzcyUyMGdyb3d0aCUyMGdyYXBoJTIwbW9kZXJufGVufDB8fHx8MTc4NDEyNTQyNnww&ixlib=rb-4.1.0&q=85",
    body: [
      { type: "p", text: "For a decade, ABM has been sold as a channel decision. Buy the platform, load the list, run the ads. In 2026 that framing is quietly falling apart — and the teams that are winning have already moved on." },
      { type: "h2", text: "The account list is the brand strategy" },
      { type: "p", text: "When you choose which 200 accounts you're going to invest against, you're not just choosing a media plan. You're choosing which customers your product roadmap will bend toward, which case studies will define your category perception, and which competitors you're implicitly declaring war on. That's a brand decision — dressed as a marketing operations one." },
      { type: "p", text: "The best CROs we work with now review the target account list quarterly, in the same room as pricing and packaging. If your ABM list still lives in a marketing spreadsheet nobody outside marketing has read, you're operating a decade behind." },
      { type: "h2", text: "Three shifts driving the reset" },
      { type: "list", items: [
        "Intent data has quietly become table stakes. The differentiation is now in how you sequence and orchestrate the response — not in who has access to the signal.",
        "Buying groups are wider (average 11 stakeholders in enterprise B2B) but the initial engagement window is narrower. That reshapes what \"tier 1 account\" even means.",
        "AI-driven personalisation has collapsed the cost of creative variation. The bottleneck is no longer content — it's the underlying account strategy the content is expressing.",
      ]},
      { type: "h2", text: "What we recommend to clients right now" },
      { type: "p", text: "Cut your target list by 40%. Yes, really. Then invest twice as much per remaining account, and hold marketing, SDRs and AEs to the same weekly account plan. It sounds simple, and it is — the reason nobody does it is that it requires actual alignment, not just a shared Miro board." },
      { type: "p", text: "Then, once a quarter, ask yourself: could I defend this account list to the CFO? Could I defend it to the product team? If the answer is no, it's a spreadsheet, not a strategy." },
    ],
  },
  {
    slug: "gtm-alignment-crisis",
    category: "Go-to-Market",
    date: "04 January 2026",
    title: "The alignment crisis: what CROs are getting wrong about RevOps",
    excerpt:
      "Alignment isn't a slogan on a townhall slide. It's a system. We unpack the six operating rituals that separate high-performing revenue orgs from the rest.",
    readTime: "11 min read",
    author: "Rana El-Sayed",
    authorRole: "Partner · Analytics & RevOps",
    image:
      "https://images.unsplash.com/photo-1523875194681-bedd468c58bf?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTN8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMGJ1c2luZXNzJTIwc3RyYXRlZ3l8ZW58MHx8fHwxNzg0MTI1NDA3fDA&ixlib=rb-4.1.0&q=85",
    body: [
      { type: "p", text: "\"We need to get marketing and sales aligned\" is the most expensive sentence in B2B. Not because alignment is hard — but because 90% of the time, the diagnosis is wrong." },
      { type: "h2", text: "Alignment is a system, not a sentiment" },
      { type: "p", text: "In every underperforming revenue org we've walked into, the same four symptoms show up: the pipeline number is disputed, the forecast is optimistic, the SDR-to-AE handoff is broken, and the marketing team is either heroes or villains depending on the quarter. None of those symptoms are cured with a bigger MarTech stack or another kickoff offsite." },
      { type: "h2", text: "The six operating rituals" },
      { type: "list", items: [
        "One weekly pipeline meeting. One agenda. One dashboard. Marketing, sales and RevOps in the same room — not three parallel status reports.",
        "A single definition of an MQL, an SQL and a qualified opportunity, agreed by the CRO and the CMO, written down, and reviewed every 90 days.",
        "Attribution model owned by RevOps, not marketing. This alone removes 80% of the political heat.",
        "Territory and account list changes governed by a quarterly ritual, not ad-hoc Slack negotiations.",
        "Deal-loss reviews run by an independent function — not the AE who lost the deal.",
        "A named executive owner for the customer journey between win and expansion. Marketing usually drops the ball here; sales assumes success.",
      ]},
      { type: "h2", text: "Start with the meeting, not the tooling" },
      { type: "p", text: "Every RevOps consulting engagement we've ever seen has recommended a technology-first path. In our experience, that's backwards. Fix the weekly meeting first — the technology decisions become obvious three months later, and you avoid buying a £400k platform to automate a workflow you were about to change anyway." },
    ],
  },
  {
    slug: "measuring-what-matters",
    category: "Analytics",
    date: "18 December 2025",
    title: "Measuring what matters — a defensible attribution model for B2B",
    excerpt:
      "Multi-touch, W-shaped, data-driven — the debate rages on. We share the pragmatic framework we use with clients to satisfy the CFO and the CMO.",
    readTime: "9 min read",
    author: "Rana El-Sayed",
    authorRole: "Partner · Analytics & RevOps",
    image:
      "https://images.unsplash.com/photo-1514557718210-26e452f8fab0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzZ8MHwxfHNlYXJjaHwzfHxtb2Rlcm4lMjBjb3Jwb3JhdGUlMjBvZmZpY2UlMjBsb25kb258ZW58MHx8fHwxNzg0MTI1NDA2fDA&ixlib=rb-4.1.0&q=85",
    body: [
      { type: "p", text: "There is no perfect attribution model. There is only the attribution model your CFO trusts, your CMO can operate against, and your board can defend to investors. Get those three right, and the specific mathematical model matters less than you think." },
      { type: "h2", text: "The three-layer model" },
      { type: "p", text: "We recommend clients think of attribution in three separable layers — each answering a different question, each owned by a different stakeholder." },
      { type: "list", items: [
        "Layer 1 — Reported: A simple, auditable rules-based model (typically W-shaped for B2B). Used for board reporting and finance. Non-negotiable, changed only annually.",
        "Layer 2 — Diagnostic: A data-driven model layered on top. Used by marketing operations to optimise mix. Can be re-tuned quarterly.",
        "Layer 3 — Directional: Qualitative post-close interviews with won deals over £250k. Used to catch what neither model can see — the dark funnel.",
      ]},
      { type: "h2", text: "Why the three layers?" },
      { type: "p", text: "Because they serve incompatible needs. The CFO needs stability. The CMO needs signal. The CRO needs truth. One model cannot do all three, and pretending it can is why so many attribution debates end in stalemate." },
      { type: "p", text: "Give each stakeholder the layer they need — and stop trying to convince the CFO that Markov-chain attribution is intuitive at 8am on a Monday board call." },
    ],
  },
  {
    slug: "buyer-journey-2026",
    category: "Strategy",
    date: "02 December 2025",
    title: "The invisible buyer journey — how 83% of B2B research now happens in the dark",
    excerpt:
      "The dark funnel is real, and it's reshaping how demand gets created. Here's how to instrument, influence, and measure what you can't directly see.",
    readTime: "7 min read",
    author: "Jonathan Reeve",
    authorRole: "Founding Partner · Strategy",
    image:
      "https://images.unsplash.com/photo-1523875194681-bedd468c58bf?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTN8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMGJ1c2luZXNzJTIwc3RyYXRlZ3l8ZW58MHx8fHwxNzg0MTI1NDA3fDA&ixlib=rb-4.1.0&q=85",
    body: [
      { type: "p", text: "Modern B2B research has moved into places your analytics stack cannot see: Slack DMs, peer forums, private podcast recommendations, WhatsApp threads. By the time a lead \"converts\" on your website, 83% of the actual buying decision has already happened somewhere you'll never track." },
      { type: "h2", text: "Instrument what you can, influence what you can't" },
      { type: "p", text: "Give up on the fantasy that first-party analytics can see everything. Instead, invest in three things: proxy signals (share-of-voice, branded search, direct traffic), qualitative post-purchase interviews, and — most importantly — the operator relationships that let you show up in those private conversations organically." },
      { type: "h2", text: "Three moves that actually work" },
      { type: "list", items: [
        "Instrument proxy signals — branded search volume, direct-load traffic, and G2/peer-review mentions are the closest we have to seeing the dark funnel breathe.",
        "Debrief every won and lost deal over £100k with a structured interview — you will hear channels and mentions that never appeared in any dashboard.",
        "Invest in a small number of deeply credible operators and community members. In 2026, one operator quietly recommending you in a private Slack outperforms most paid campaigns.",
      ]},
      { type: "h2", text: "The best 'ad' in 2026 is a good sentence in a Slack DM" },
      { type: "p", text: "Which is another way of saying: the best marketing investment we see clients make right now is in a small number of deeply credible operators — not in a bigger media plan." },
    ],
  },
  {
    slug: "creative-that-converts",
    category: "Creative",
    date: "20 November 2025",
    title: "Creative that converts — the anatomy of a category-defining B2B campaign",
    excerpt:
      "Great B2B creative doesn't ask for permission. We break down five campaigns that shaped their categories — and the operating principles behind them.",
    readTime: "10 min read",
    author: "Marcus Alderton",
    authorRole: "Partner · Creative & Content",
    image:
      "https://images.unsplash.com/photo-1514557718210-26e452f8fab0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzZ8MHwxfHNlYXJjaHwzfHxtb2Rlcm4lMjBjb3Jwb3JhdGUlMjBvZmZpY2UlMjBsb25kb258ZW58MHx8fHwxNzg0MTI1NDA2fDA&ixlib=rb-4.1.0&q=85",
    body: [
      { type: "p", text: "B2B creative has an inferiority complex it has never earned. The idea that enterprise buyers can't handle bold, opinionated, funny work is a story sold to marketing teams by risk-averse agencies. It is empirically wrong — and the campaigns that shape categories are the proof." },
      { type: "h2", text: "Five principles" },
      { type: "list", items: [
        "Have a point of view — a real one, not a positioning statement laundered into a headline.",
        "Design for the boardroom and the shop floor. Enterprise buyers screenshot campaigns to their peers. If it doesn't survive that Slack, it doesn't work.",
        "Sacrifice reach. Speak to the 200 people who matter, not the 200,000 who don't.",
        "Own a format. A recurring signature — a podcast, a study, a live event — beats a thousand disposable posts.",
        "Measure the response, not the impression. A single 'holy shit' from a Fortune 500 CMO is worth more than 10 million ad views.",
      ]},
      { type: "h2", text: "Category-definers are rarely first" },
      { type: "p", text: "Almost every category-defining B2B campaign we admire was launched into an existing crowded space. What set them apart wasn't originality of format — it was clarity of conviction. If your creative team can't articulate the argument in one sentence, no amount of production value will rescue the campaign." },
    ],
  },
];

export const CLIENTS = [
  "Halcyon", "Northwind", "Fjord", "Meridian", "Atlas Labs", "Kestrel", "Vantage", "Lumen", "Orbital", "Bramwell",
];

export const STATS = [
  { value: "£340M+", label: "Client pipeline generated" },
  { value: "18", label: "Countries served" },
  { value: "94%", label: "Client retention rate" },
  { value: "2013", label: "Founded in Berlin" },
];

// ---------------------------------------------------------------------------
// Company profile — RevGrow UG (Unternehmergesellschaft haftungsbeschränkt)
// A Berlin-based go-to-market advisory, founded 2013.
// ---------------------------------------------------------------------------
export const COMPANY = {
  legalName: "RevGrow UG (haftungsbeschränkt)",
  shortName: "RevGrow",
  foundedYear: 2013,
  city: "Berlin",
  country: "Germany",
  address: {
    line1: "Akazienstraße 3A",
    postal: "10823 Berlin",
    country: "Germany",
  },
  phone: "+49 30 55235449",
  phoneHref: "+493055235449",
  email: "sales@revgrow.co.uk",
};

// ---------------------------------------------------------------------------
// Team — sourced from the RevGrow team page and grouped by department.
// Portraits are all served locally from /public/team/<slug>.png. Non-Sunjay
// portraits are AI-generated (Gemini Nano Banana) using Sunjay's real photo
// as an aesthetic reference so the whole grid feels visually cohesive.
// ---------------------------------------------------------------------------

export const TEAM = [
  // Leadership
  {
    slug: "sunjay-ramaswamy",
    name: "Sunjay Ramaswamy",
    role: "Chief Business Officer",
    department: "Leadership",
    photo: "/team/sunjay.png",
    short: "Brings a strategic blend of commercial acumen, marketing expertise, and growth-focused leadership.",
  },
  {
    slug: "emily-carrington",
    name: "Emily Carrington",
    role: "Managing Director",
    department: "Leadership",
    photo: "/team/emily-carrington.png",
    short: "Leads with a clear vision to help businesses unlock their full growth potential through strategic marketing.",
  },
  {
    slug: "jamal-shah",
    name: "Jamal Shah",
    role: "Strategy Director",
    department: "Leadership",
    photo: "/team/jamal-shah.png",
    short: "The architect behind innovative, results-driven growth strategies — with a sharp analytical mind.",
  },
  {
    slug: "sophie-patel",
    name: "Sophie Patel",
    role: "Head of Operations",
    department: "Leadership",
    photo: "/team/sophie-patel.png",
    short: "Ensures ambitious growth strategies are backed by seamless execution and operational excellence.",
  },

  // Creative
  {
    slug: "liam-oconnor",
    name: "Liam O'Connor",
    role: "Creative Director",
    department: "Creative",
    photo: "/team/liam-oconnor.png",
    short: "Leads creative vision — blending bold ideas with strategic clarity to craft campaigns that resonate.",
  },
  {
    slug: "chioma-adeyemi",
    name: "Chioma Adeyemi",
    role: "Senior Copywriter",
    department: "Creative",
    photo: "/team/chioma-adeyemi.png",
    short: "The voice behind compelling messaging that transforms complex ideas into clear, engaging narratives.",
  },
  {
    slug: "tomas-garcia",
    name: "Tomás García",
    role: "Graphic Designer",
    department: "Creative",
    photo: "/team/tomas-garcia.png",
    short: "Brings visual concepts to life — translating brand strategies into impactful designs that capture attention.",
  },

  // Digital Marketing
  {
    slug: "aisha-khan",
    name: "Aisha Khan",
    role: "Social Media Manager",
    department: "Digital Marketing",
    photo: "/team/aisha-khan.png",
    short: "Drives digital presence across platforms — creating content that sparks conversation and builds communities.",
  },
  {
    slug: "ben-whittaker",
    name: "Ben Whittaker",
    role: "SEO Specialist",
    department: "Digital Marketing",
    photo: "/team/ben-whittaker.png",
    short: "Ensures brands get discovered by the right audience at the right time through strategic SEO.",
  },
  {
    slug: "lucie-dubois",
    name: "Lucie Dubois",
    role: "PPC Campaign Manager",
    department: "Digital Marketing",
    photo: "/team/lucie-dubois.png",
    short: "Crafts high-performing paid advertising campaigns that deliver measurable returns and business growth.",
  },

  // Client Services
  {
    slug: "rachel-greenfield",
    name: "Rachel Greenfield",
    role: "Account Director",
    department: "Client Services",
    photo: "/team/rachel-greenfield.png",
    short: "Brings a strategic mind and passion for client success — with over a decade of account management experience.",
  },
  {
    slug: "amir-rahman",
    name: "Amir Rahman",
    role: "Client Services Manager",
    department: "Client Services",
    photo: "/team/amir-rahman.png",
    short: "Dedicated to building strong, trusted relationships — ensuring every client feels supported and valued.",
  },
  {
    slug: "jessie-lin",
    name: "Jessie Lin",
    role: "Junior Account Executive",
    department: "Client Services",
    photo: "/team/jessie-lin.png",
    short: "Brings fresh energy, attention to detail, and a passion for helping clients succeed in account operations.",
  },

  // Analytics
  {
    slug: "marcus-taylor",
    name: "Marcus Taylor",
    role: "Head of Data & Insights",
    department: "Analytics",
    photo: "/team/marcus-taylor.png",
    short: "Turns complex data into clear, actionable insights that drive smarter business decisions.",
  },
  {
    slug: "nina-petrov",
    name: "Nina Petrov",
    role: "Marketing Analyst",
    department: "Analytics",
    photo: "/team/nina-petrov.png",
    short: "Brings a keen eye for detail and a passion for uncovering the stories hidden within data.",
  },
];

export const DEPARTMENTS = [
  "Leadership",
  "Creative",
  "Digital Marketing",
  "Client Services",
  "Analytics",
];
